import React, { Fragment } from 'react'
import pure from 'recompose/pure'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Typography from '@material-ui/core/Typography'
import FormHelperText from '@material-ui/core/FormHelperText'
import withStyles from '@material-ui/core/styles/withStyles'
import { Field, reduxForm, SubmissionError } from 'redux-form'
import Input from '@material-ui/core/Input'
import LinearProgress from '@material-ui/core/LinearProgress'
import CheckIcon from 'react-icons/lib/io/android-checkmark-circle'
import ErrorIcon from 'react-icons/lib/md/error-outline'
import compose from 'recompose/compose'
import cx from 'classnames'
import { sendAlert, subscribe } from 'actions/app'
import regexp from 'utils/regexp'

const styles = (theme) => ({
  form: {
    position: 'relative',
    width: '100%',
    backgroundColor: 'rgba(34, 34, 34, 0.95)',
    padding: 18,
    [theme.breakpoints.down('xs')]: {
      padding: 13,
      width: '100%',
      fontSize: '0.95em',
    },
    '& > button': {
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.65em',
        color: 'red',
      },
    },
  },
  bg: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    content: '',
    opacity: 1,
    zIndex: -1,
    background:
      'url(https://s3-us-west-1.amazonaws.com/healthfulinspired/app/woman-800x533.jpg)',
    backgroundPosition: 'top 10% left 50%',
  },
  header: {
    padding: 8,
    fontWeight: 400,
    color: '#fff',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  inputs: {
    marginBottom: 5,
    width: '50%',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: 7,
      marginBottom: 3,
    },
  },
  helperText: {
    fontSize: '0.9em',
    marginBottom: 5,
    textAlign: 'center',
    color: '#fff',
    fontWeight: 300,
  },
  emailInput: {
    float: 'left',
    fontFamily: 'Roboto',
    height: '100%',
    maxHeight: 36,
    fontWeight: 400,
    width: '50%',
    background: theme.palette.background.light,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.borders.cloudier}`,
    '&::placeholder': {
      fontFamily: 'Roboto',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
      maxHeight: 33,
    },
  },
  subscribeButton: {
    float: 'right',
    fontFamily: 'Roboto',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '1em',
    background: theme.palette.success,
    border: 0,
    padding: 8,
    color: '#fff',
    '&:hover': {
      background: theme.palette.secondary.main,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
      padding: 9,
    },
  },
  successText: {
    textAlign: 'center',
    color: theme.palette.successLighter,
    fontSize: '0.8em',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  errorText: {
    fontFamily: 'Roboto',
    textAlign: 'center',
    color: theme.palette.error.light,
    padding: 0,
    fontSize: '0.8em',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  checkIcon: {
    color: theme.palette.successLighter,
    fontSize: '2.5em',
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: '2.5em',
  },
})

const renderInputField = ({
  input,
  meta: { touched, error },
  errorTextClassName,
  ...customProps
}) => (
  <Fragment>
    {touched && error && <div className={errorTextClassName}>{error}</div>}
    <Input error={touched && !!error} {...input} {...customProps} />
  </Fragment>
)

const SubscribeFormInArticle = ({
  classes,
  submitting,
  handleSubmit,
  submitSucceeded,
  error,
  text,
  cta,
}) => {
  const actionProps = {}
  let subscribeText,
    subscribeCtaText,
    oldBrowser = false

  if (
    typeof Promise !== 'undefined' &&
    Promise.toString().indexOf('[native code]') !== -1
  ) {
    actionProps.onSubmit = handleSubmit
  } else {
    oldBrowser = true
    actionProps.action = process.env.SUBSCRIBE_URL
    actionProps.method = 'post'
  }

  if (!text) {
    subscribeText =
      'Like our content? Become a subscriber today to start receiving the latest, best trending health tips delivered straight to your inbox.'
  } else {
    subscribeText = text
  }

  if (!cta) {
    subscribeCtaText = 'Subscribe me!'
  } else {
    subscribeCtaText = cta
  }

  return (
    <form
      style={{ opacity: submitting ? 0.4 : 1 }}
      className={classes.form}
      {...actionProps}
    >
      <div className={classes.bg} />
      <Typography
        variant="subheading"
        className={classes.header}
        align="center"
      >
        {subscribeText}
      </Typography>
      <div>
        <LinearProgress
          color="secondary"
          style={{
            position: 'relative',
            width: '100%',
            marginBottom: 5,
            display: submitting ? 'block' : 'none',
          }}
        />
        {!submitSucceeded && (
          <div>
            <FormHelperText className={classes.helperText}>
              ALL we need is your email address, it's dead simple!
            </FormHelperText>
            {!oldBrowser && (
              <Field
                errorTextClassName={classes.errorText}
                inputProps={{
                  className: classes.inputs,
                }}
                className={classes.emailInput}
                name="email"
                type="email"
                component={renderInputField}
                placeholder="Your email"
                disableUnderline
                required
              />
            )}
            {oldBrowser && (
              <Input
                inputProps={{ className: classes.inputs }}
                className={classes.emailInput}
                name="email"
                type="email"
                placeholder="Your email"
                disableUnderline
                required
              />
            )}
            {oldBrowser && (
              <Fragment>
                <input
                  type="hidden"
                  name="thankyou_url"
                  value={window.location.href}
                />
                <input type="hidden" name="forward_data" value="get" />
              </Fragment>
            )}
            <input type="hidden" name="campaign_token" value="6qdpg" />
            <input
              className={cx(classes.inputs, classes.subscribeButton)}
              type="submit"
              value={subscribeCtaText}
            />
          </div>
        )}
        <div style={{ clear: 'both' }}>
          {submitSucceeded &&
            !error && (
              <Typography variant="caption" className={classes.successText}>
                <CheckIcon className={classes.checkIcon} /> Thank you, you're
                all set. Enjoy your read!
              </Typography>
            )}
          {error && (
            <Typography className={classes.errorText}>
              <ErrorIcon className={classes.errorIcon} />
              {error}
            </Typography>
          )}
        </div>
      </div>
    </form>
  )
}

SubscribeFormInArticle.propTypes = {
  classes: PropTypes.object.isRequired,
  subscribe: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
  text: PropTypes.string,
  cta: PropTypes.string,
}

export default compose(
  withStyles(styles),
  connect(null, { subscribe, sendAlert }),
  reduxForm({
    form: 'SubscribeFormInArticle',
    onSubmit: (values, _, { subscribe: subscribeUser, reset, sendAlert }) =>
      subscribeUser({ campaignToken: '6qdpg', ...values })
        .then(() => {
          sendAlert({
            message: "Awesome! You're now subscribed.",
            type: 'success',
          })
          return reset()
        })
        .catch((error) => {
          console.error(error)
          const message =
            'Either the email is already registered or an error occurred. Please try again later.'
          sendAlert({ message, type: 'error' })
          throw new SubmissionError({ _error: message })
        }),
    validate: ({ email }) => {
      const errors = {}
      if (!email) {
        errors.email = 'Please provide your email.'
      }
      if (!regexp.email.test(email)) {
        errors.email = 'Please provide a valid email.'
      }
      return errors
    },
  }),
  pure,
)(SubscribeFormInArticle)



// WEBPACK FOOTER //
// ./src/components/forms/SubscribeFormInArticle.js